import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, User, CreditCard, Key, Pencil } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import PageSEO from '../components/PageSEO';

export default function UserSettings({ user, onProfileUpdate }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(false);

  // Form states
  const [firstName, setFirstName] = useState(user?.first_name || user?.name?.split(' ')[0] || '');
  const [lastName, setLastName] = useState(user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '');
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState(false);
  const [billingRetry, setBillingRetry] = useState(0);

  // Change Password state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || user.name?.split(' ')[0] || '');
      setLastName(user.last_name || user.name?.split(' ').slice(1).join(' ') || '');
    }
  }, [user]);

  useEffect(() => {
    if (activeTab !== 'access') return;
    let cancelled = false;
    setBilling(null);
    setBillingLoading(true);
    setBillingError(false);
    axios.get('/billing/summary').then(({ data }) => {
      if (!cancelled) setBilling(data);
    }).catch(() => {
      if (!cancelled) setBillingError(true);
    }).finally(() => {
      if (!cancelled) setBillingLoading(false);
    });
    return () => { cancelled = true; };
  }, [activeTab, user?.id, billingRetry]);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      const res = await axios.put('/auth/profile', {
        name: fullName,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });

      const updatedUser = res.data.tenant;
      onProfileUpdate(updatedUser);
      toast.success('Account settings saved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password updated successfully!');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const initialLetter = (firstName?.[0] || user?.name?.[0] || 'U').toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background transition-colors p-4 sm:p-6 lg:p-12">
      <PageSEO title="Account Settings | AI Search Advisers" isPrivate={true} />
      {/* ── Top Header Bar ── */}
      <div className="max-w-6xl mx-auto mb-6 sm:mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl sm:text-3xl font-bold font-fraunces text-slate-900 dark:text-white mb-1">My account</h3>
          <p className="text-slate-600 dark:text-slate-400 text-sm">Manage your personal profile, subscription, and security options.</p>
        </div>
        <div className="flex w-full sm:w-auto shrink-0 items-center gap-3">
          <ThemeToggle />
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="flex-1 sm:flex-none min-h-11 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </div>

      {/* ── Main Layout Grid ── */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
        {/* ── Left Sidebar Navigation ── */}
        <div className="grid grid-cols-2 lg:block gap-2 lg:space-y-1.5">
          <button
            type="button"
            onClick={() => setActiveTab('account')}
            className={`w-full text-left px-3 sm:px-4 py-3 rounded-xl font-medium text-sm transition-all flex items-center gap-3 ${
              activeTab === 'account'
                ? 'bg-[#e8ded6] dark:bg-slate-800 text-slate-900 dark:text-white font-bold shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/40'
            }`}
          >
            <User className="w-4 h-4 text-slate-500" />
            <span>My account</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('access')}
            className={`w-full text-left px-3 sm:px-4 py-3 rounded-xl font-medium text-sm transition-all flex items-center gap-3 ${
              activeTab === 'access'
                ? 'bg-[#e8ded6] dark:bg-slate-800 text-slate-900 dark:text-white font-bold shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/40'
            }`}
          >
            <CreditCard className="w-4 h-4 text-slate-500" />
            <span>Access & Billing</span>
          </button>
        </div>

        {/* ── Main Content Area ── */}
        <div className="min-w-0 lg:col-span-3">
          {/* TAB 1: MY ACCOUNT */}
          {activeTab === 'account' && (
            <Card className="bg-white dark:bg-card rounded-2xl p-4 sm:p-6 xl:p-8 border border-slate-200 dark:border-border shadow-md dark:shadow-none">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6">Account settings</h2>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 xl:gap-8">
                {/* Account Fields (2 cols) */}
                <div className="min-w-0 xl:col-span-2 space-y-6">
                  {/* Email & Password */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5 block">Email address</Label>
                      <div className="relative">
                        <Input
                          type="email"
                          value={user?.email || ''}
                          readOnly
                          aria-label="Email address (read-only)"
                          className="bg-slate-100/80 dark:bg-slate-800 border-none rounded-xl pr-10 text-slate-900 dark:text-white font-medium"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5 block">Password</Label>
                      <div className="relative">
                        <Input
                          type="password"
                          readOnly
                          value="••••••••"
                          className="bg-slate-100/80 dark:bg-slate-800 border-none rounded-xl pr-10 text-slate-900 dark:text-white font-medium cursor-pointer"
                          onClick={() => setShowPasswordModal(true)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswordModal(true)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-white"
                          title="Change password"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Names */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5 block">First name</Label>
                      <Input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-medium"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5 block">Last name</Label>
                      <Input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-medium"
                      />
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="pt-2">
                    <Button
                      onClick={handleSaveProfile}
                      disabled={loading}
                      className="w-full sm:w-auto min-h-11 bg-[#d9cfc7] dark:bg-[#FEAB3D] text-slate-900 dark:text-[#021426] hover:opacity-90 font-bold px-6 py-2.5 rounded-xl shadow-sm transition-all"
                    >
                      {loading ? 'Saving...' : 'Save changes'}
                    </Button>
                  </div>
                </div>

                {/* Avatar Box */}
                <div>
                  <div className="border border-slate-200 dark:border-slate-800 rounded-2xl p-6 flex flex-col items-center justify-center text-center bg-slate-50/50 dark:bg-slate-800/20">
                    <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-4 self-start">Avatar</Label>
                    <div className="w-24 h-24 rounded-full bg-[#2563eb] text-white flex items-center justify-center font-bold text-4xl shadow-md mb-4">
                      {initialLetter}
                    </div>
                    <p className="break-words text-xs text-slate-500 dark:text-slate-400 font-medium">
                      Account Initial: <span className="font-bold text-slate-800 dark:text-slate-200">{firstName}</span>
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* TAB 2: ACCESS & BILLING */}
          {activeTab === 'access' && (
            <Card className="bg-white dark:bg-card rounded-2xl p-4 sm:p-6 xl:p-8 border border-slate-200 dark:border-border shadow-md dark:shadow-none space-y-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Access & Subscription Plan</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">View your account access, subscription details, and tracked event usage.</p>

              {billingLoading && <p role="status" className="text-sm text-slate-600 dark:text-slate-400">Loading your plan and usage…</p>}
              {billingError && (
                <div role="alert" className="space-y-3">
                  <p>We couldn't load your billing details.</p>
                  <Button variant="outline" onClick={() => setBillingRetry(value => value + 1)}>Try again</Button>
                </div>
              )}
              {billing && (
                <>
                  <div className="p-4 sm:p-6 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="min-w-0 space-y-2">
                      <h3 className="text-2xl font-bold text-slate-900 dark:text-white capitalize">{billing.plan.replace(/[_-]/g, ' ')} Plan</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 capitalize">Account access: {billing.account_status}</p>
                      {billing.subscription_plan && <p className="text-sm">Subscription: {billing.subscription_plan}</p>}
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {billing.billing_status === 'unavailable' ? 'Billing details are temporarily unavailable.' :
                          billing.billing_status === 'not_connected' ? 'No billing account connected. Renewal details are not available.' :
                          `Subscription status: ${(billing.subscription_status || 'unavailable').replace(/_/g, ' ')}`}
                      </p>
                      {billing.billing_interval && <p className="text-sm text-slate-600 dark:text-slate-400">Billing cycle: every {billing.billing_interval_count} {billing.billing_interval}{billing.billing_interval_count > 1 ? 's' : ''}</p>}
                      {billing.period_end && <p className="text-sm text-slate-600 dark:text-slate-400">
                        {billing.cancel_at_period_end ? 'Scheduled to end' : ['active', 'trialing'].includes(billing.subscription_status) ? (billing.subscription_status === 'trialing' ? 'Trial ends' : 'Next renewal') : 'Billing period ends'}: {new Date(billing.period_end).toLocaleDateString()}
                      </p>}
                    </div>
                    <Button onClick={() => navigate('/pricing')} className="w-full sm:w-auto shrink-0 min-h-11 bg-[#FEAB3D] text-[#021426] font-bold px-6 py-2.5 rounded-xl hover:bg-[#e8960a]">View Plans</Button>
                  </div>
                  <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white">Usage this calendar month (UTC)</h3>
                    <div className="flex flex-wrap gap-2 justify-between text-sm font-medium">
                      <span className="text-slate-600 dark:text-slate-400">Tracked Events</span>
                      <span className="text-slate-900 dark:text-white font-bold">{billing.usage.tracked_events.toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Across your projects. No event quota is configured for this account.</p>
                  </div>
                </>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* ── Change Password Modal ── */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white dark:bg-card border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
              <Key className="w-5 h-5 text-[#FEAB3D]" /> Change Password
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">Enter your current password and a new secure password.</p>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 block">Current Password</Label>
                <Input
                  type="password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="border border-slate-300 dark:border-slate-700 rounded-xl"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 block">New Password</Label>
                <Input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="border border-slate-300 dark:border-slate-700 rounded-xl"
                  placeholder="At least 8 characters"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 block">Confirm New Password</Label>
                <Input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="border border-slate-300 dark:border-slate-700 rounded-xl"
                  placeholder="Confirm new password"
                />
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPasswordModal(false)}
                  className="rounded-xl border-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={passwordLoading}
                  className="bg-[#021426] dark:bg-[#FEAB3D] text-white dark:text-[#021426] font-bold rounded-xl px-6"
                >
                  {passwordLoading ? 'Updating...' : 'Update Password'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
